import { css } from '@emotion/react';
import styled from '@emotion/styled';
import React from 'react';
import { Currency } from '../../../types/PaymentTypes';
import { microcopySet, useMicrocopy } from '../../../utils/contentfulMicrocopy';
import { getCurrencySymbol } from '../../../utils/helpers/currency';
import { colors, spacing } from '../../../utils/styleguide';
import Typography, { variants } from '../text/Typography';

const InfoBox = styled(Typography)`
  background-color: ${colors.lightRed};
  color: ${colors.error};
  margin-top: ${spacing[1]}px;
  padding: ${spacing[1]}px ${spacing[2]}px;
`;

const CurrencyLabel = styled(Typography)`
  bottom: 0;
  color: ${colors.black};
  display: block;
  font-size: 11px;
  padding: ${spacing[3]}px;
  position: absolute;
  right: 0;
  top: 0;
`;

const StyledInput = styled.input`
  ${variants.inputField.css}
  border: 1px solid  ${colors.gray};
  flex: 1;
  padding: ${spacing[3]}px;

  &.invalid,
  &:focus.invalid {
    border-color: ${colors.error};
  }

  &.invalid + ${CurrencyLabel} {
    color: ${colors.error};
  }

  ::placeholder {
    color: ${colors.gray};
  }

  :focus {
    border-color: ${colors.black};
    outline: none;
  }

  :focus::placeholder {
    color: transparent;
  }

  :placeholder-shown + ${CurrencyLabel} {
    display: none;
  }

  :invalid {
    colors: ${colors.error};
  }

  /* Remove number arrows */
  -moz-appearance: textfield;
  ::-webkit-outer-spin-button,
  ::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

type Props = {
  amount?: number;
  currency: Currency;
  className?: string;
  errorMessage?: string;
  isMonthlyPayment: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus: (event: React.FocusEvent<HTMLInputElement>) => void;
  placeholder?: string;
};

export default function AmountInput({
  amount,
  currency,
  className,
  errorMessage,
  isMonthlyPayment,
  onChange,
  onFocus,
  placeholder,
}: Props) {
  const currencySymbol = currency?.symbol ?? getCurrencySymbol(currency.code);
  const t = useMicrocopy();
  const month = t(microcopySet.PAYMENT_OPTION, 'month');
  const label = `${currencySymbol}${isMonthlyPayment ? `/${month}` : ''}`;

  return (
    <div
      className={className}
      css={css`
        display: flex;
        flex-direction: column;
        position: relative;
      `}
    >
      <StyledInput
        className={errorMessage ? 'invalid' : ''}
        onChange={onChange}
        onFocus={onFocus}
        placeholder={placeholder}
        type="number"
        value={amount}
        min={0}
      />

      <CurrencyLabel variant="label">{label}</CurrencyLabel>

      {errorMessage && <InfoBox variant="detailBold">{errorMessage}</InfoBox>}
    </div>
  );
}
